import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtMXN, fmtDate, ordenPendienteCobro, entregaCompleta } from "@/lib/api";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import OrderStateBadge from "@/components/OrderStateBadge";
import { Search, BadgeDollarSign, Banknote, CreditCard, Smartphone, Layers } from "lucide-react";
import { toast } from "sonner";

function CobrarDialog({ orden, cuentaId, open, onOpenChange, onCobrado }) {
  const [metodo, setMetodo] = useState("EFECTIVO");
  const [recibido, setRecibido] = useState("");
  const [detalle, setDetalle] = useState({ efectivo: 0, tarjeta: 0, transferencia: 0 });

  // Soporte de cuenta separada: si cuentaId, usar el total de esa cuenta
  const cuenta = cuentaId && orden ? (orden.cuentas || []).find((c) => c.id === cuentaId) : null;
  const totalACobrar = cuenta ? cuenta.total : (orden?.total || 0);

  useEffect(() => {
    if (open && orden) {
      setMetodo("EFECTIVO");
      setRecibido(totalACobrar.toString());
      setDetalle({ efectivo: totalACobrar, tarjeta: 0, transferencia: 0 });
    }
  }, [open, orden, totalACobrar]);

  if (!orden) return null;
  const cambio = Math.max(0, parseFloat(recibido || 0) - totalACobrar);

  const cobrar = async () => {
    try {
      const payload = { metodo, monto_recibido: parseFloat(recibido || 0) };
      if (metodo === "MIXTO") {
        payload.detalle = detalle;
        payload.monto_recibido = (parseFloat(detalle.efectivo) || 0) + (parseFloat(detalle.tarjeta) || 0) + (parseFloat(detalle.transferencia) || 0);
      }
      const url = cuentaId
        ? `/ordenes/${orden.id}/cuentas/${cuentaId}/cobrar`
        : `/ordenes/${orden.id}/cobrar`;
      const r = await api.post(url, payload);
      toast.success(`Cobrado. Cambio: ${fmtMXN(r.data.cambio)}`);
      onCobrado();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{cuenta ? `Cobrar ${cuenta.nombre} (${orden.numero})` : `Cobrar ${orden.numero}`}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="bg-secondary rounded-lg p-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Total a cobrar</div>
            <div className="text-4xl font-black text-primary">{fmtMXN(totalACobrar)}</div>
          </div>

          <Tabs value={metodo} onValueChange={setMetodo}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="EFECTIVO" className="gap-1" data-testid="metodo-efectivo"><Banknote className="h-3 w-3" />Efec.</TabsTrigger>
              <TabsTrigger value="TARJETA" className="gap-1" data-testid="metodo-tarjeta"><CreditCard className="h-3 w-3" />Tarj.</TabsTrigger>
              <TabsTrigger value="TRANSFERENCIA" className="gap-1" data-testid="metodo-transferencia"><Smartphone className="h-3 w-3" />Tran.</TabsTrigger>
              <TabsTrigger value="MIXTO" className="gap-1" data-testid="metodo-mixto"><Layers className="h-3 w-3" />Mixto</TabsTrigger>
            </TabsList>

            <TabsContent value="EFECTIVO" className="space-y-2">
              <Label>Monto recibido</Label>
              <Input type="number" step="0.01" value={recibido} onChange={(e) => setRecibido(e.target.value)} className="text-2xl h-14 text-center font-bold" data-testid="monto-recibido-input" />
              <div className="text-center text-sm">Cambio: <span className="font-bold text-emerald-500">{fmtMXN(cambio)}</span></div>
              <div className="flex gap-1 flex-wrap">
                {[totalACobrar, 100, 200, 500, 1000].map((v) => (
                  <Button key={v} size="sm" variant="outline" onClick={() => setRecibido(v.toString())}>{fmtMXN(v)}</Button>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="TARJETA" className="space-y-2">
              <Label>Monto cobrado</Label>
              <Input type="number" step="0.01" value={recibido} onChange={(e) => setRecibido(e.target.value)} className="text-2xl h-14 text-center font-bold" />
            </TabsContent>
            <TabsContent value="TRANSFERENCIA" className="space-y-2">
              <Label>Monto recibido</Label>
              <Input type="number" step="0.01" value={recibido} onChange={(e) => setRecibido(e.target.value)} className="text-2xl h-14 text-center font-bold" />
            </TabsContent>
            <TabsContent value="MIXTO" className="space-y-2">
              <div><Label>Efectivo</Label><Input type="number" step="0.01" value={detalle.efectivo} onChange={(e) => setDetalle({ ...detalle, efectivo: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Tarjeta</Label><Input type="number" step="0.01" value={detalle.tarjeta} onChange={(e) => setDetalle({ ...detalle, tarjeta: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Transferencia</Label><Input type="number" step="0.01" value={detalle.transferencia} onChange={(e) => setDetalle({ ...detalle, transferencia: parseFloat(e.target.value) || 0 })} /></div>
              <div className="text-xs text-muted-foreground">Suma: {fmtMXN((detalle.efectivo || 0) + (detalle.tarjeta || 0) + (detalle.transferencia || 0))}</div>
            </TabsContent>
          </Tabs>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={cobrar} className="min-w-[140px]" data-testid="confirmar-cobro-btn">Confirmar cobro</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Caja() {
  const [ordenes, setOrdenes] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [selectedCuentaId, setSelectedCuentaId] = useState(null);
  const [cobrarOpen, setCobrarOpen] = useState(false);
  const [searchParams] = useSearchParams();

  const load = () => api.get("/ordenes", { params: { cobrada: false, only_today: true, limit: 200 } }).then((r) => setOrdenes(r.data.items || []));
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, []);
  useRealtimeEvents((msg) => { if (msg?.type?.startsWith("orden.")) load(); }, () => load());

  useEffect(() => {
    const orderId = searchParams.get("orden");
    const cuentaId = searchParams.get("cuenta");
    if (orderId && ordenes.length > 0) {
      const o = ordenes.find((x) => x.id === orderId);
      if (o) {
        setSelected(o);
        setSelectedCuentaId(cuentaId || null);
        setCobrarOpen(true);
      }
    }
  }, [searchParams, ordenes]);

  // Regla central: Caja muestra órdenes pendientes de cobro (no cobradas y no anuladas).
  // Una orden YA ENTREGADA pero NO COBRADA también debe aparecer aquí — caja debe cobrarla.
  const filtered = useMemo(() => ordenes.filter((o) =>
    ordenPendienteCobro(o) && (
      !q || o.numero?.toLowerCase().includes(q.toLowerCase()) ||
      o.mesa_nombre?.toLowerCase().includes(q.toLowerCase()) ||
      o.cliente_nombre?.toLowerCase().includes(q.toLowerCase())
    )
  ), [ordenes, q]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-black tracking-tight">Caja</h2>
        <p className="text-sm text-muted-foreground mt-1">Pendientes de cobro de hoy.</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Buscar por número o mesa…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-12" data-testid="caja-search-input" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((o) => {
          const cuentas = o.cuentas || [];
          const tieneCuentas = cuentas.length > 0;
          return (
          <Card key={o.id} className="hover:border-primary transition-colors" data-testid={`caja-orden-${o.id}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="font-black text-lg">{o.numero}</div>
                <OrderStateBadge state={o.estado} />
              </div>
              <div className="mt-2 text-sm text-muted-foreground">
                {o.mesa_nombre || o.cliente_nombre || "Pedido"}
              </div>
              {o.created_by_nombre && (
                <div className="text-[10px] text-muted-foreground truncate" title={o.created_by_nombre}>
                  Por: {o.created_by_nombre}
                </div>
              )}
              <div className="mt-3 text-3xl font-black text-primary">{fmtMXN(o.total)}</div>
              <div className="text-xs text-muted-foreground mt-1">{o.items.length} producto(s) · {o.tipo}</div>
              {entregaCompleta(o) && (
                <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-200 dark:bg-emerald-900/60 text-emerald-900 dark:text-emerald-100" data-testid={`caja-lista-cobro-${o.id}`}>
                  ✓ Entregada · lista para cobrar
                </div>
              )}

              {tieneCuentas ? (
                <div className="mt-4 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Cuentas separadas</div>
                  {cuentas.map((c) => (
                    <Button
                      key={c.id}
                      className="w-full justify-between"
                      variant={c.cobrada ? "ghost" : "outline"}
                      disabled={c.cobrada}
                      onClick={() => { setSelected(o); setSelectedCuentaId(c.id); setCobrarOpen(true); }}
                      data-testid={`caja-cuenta-${c.id}`}
                    >
                      <span className="truncate">{c.cobrada ? "✓" : "→"} {c.nombre}</span>
                      <span className="font-bold">{fmtMXN(c.total)}</span>
                    </Button>
                  ))}
                </div>
              ) : (
                <Button className="w-full mt-4 h-12 text-base font-bold" onClick={() => { setSelected(o); setSelectedCuentaId(null); setCobrarOpen(true); }} data-testid={`cobrar-btn-${o.id}`}>
                  <BadgeDollarSign className="h-5 w-5 mr-2" />Cobrar
                </Button>
              )}
            </CardContent>
          </Card>
        );
        })}
        {filtered.length === 0 && <div className="col-span-full text-center text-muted-foreground py-12">No hay órdenes pendientes de cobro.</div>}
      </div>

      <CobrarDialog orden={selected} cuentaId={selectedCuentaId} open={cobrarOpen} onOpenChange={(o) => { setCobrarOpen(o); if (!o) setSelectedCuentaId(null); }} onCobrado={load} />
    </div>
  );
}
